<template>
  <v-container>
    <!-- META-DATA -->
    <vue-headful
      :title="'Shop Hats' + this.$store.state.app.meta.titleSuffix"
      description="Get the perfect MemerHat to showcase your patches and personality." />

    <!-- HAT CATALOG CONTROLS -->
    <div
      class="hat-catalog-toolbar"
      :class="{
        'hat-catalog-toolbar--above-drawer': !filterDrawer
      }">
      <v-btn
        flat
        class="hat-catalog-toolbar__filter-button"
        @click.stop="filterDrawer = !filterDrawer">
        <v-icon>
          tune
        </v-icon>
        filters
      </v-btn>
      <h2 class="title">Hats</h2>
      <v-select
        v-model="currentSort"
        :items="sortCriteria"
        label="sort by"
        class="hat-catalog-sort"
        return-object />
    </div>

    <div class="hat-catalog-layout">
      <!-- FILTER DRAWER -->
      <div
        class="hat-filter-drawer__wrapper"
        :class="{
          'hat-filter-drawer__wrapper--active': filterDrawer
        }">
        <v-navigation-drawer
          v-model="filterDrawer"
          mobile-break-point="768"
          width="250"
          class="hat-filter-drawer">
          <div class="hat-filters">
            <div class="hat-filters__header-row">
              <h4 class="hat-filters__header-title title">
                Filters
              </h4>
              <v-btn
                icon
                class="hat-filter-drawer__close-button"
                @click.stop="filterDrawer = !filterDrawer">
                <v-icon>
                  close
                </v-icon>
              </v-btn>
            </div>
            <v-divider />
            <div class="hat-filters__body">
              <div
                v-ripple
                class="hat-filters__row"
                @click="filters.inStock = !filters.inStock">
                <v-checkbox
                  v-model="filters.inStock"
                  color="primary"
                  class="hat-filters__checkbox"
                  label="in stock"
                  hide-details
                  @click="filters.inStock = !filters.inStock" />
              </div>
              <div
                v-ripple
                class="hat-filters__row"
                @click="filters.onSale = !filters.onSale">
                <v-checkbox
                  v-model="filters.onSale"
                  color="primary"
                  class="hat-filters__checkbox"
                  label="on sale"
                  hide-details
                  @click="filters.onSale = !filters.onSale" />
              </div>
              <div class="hat-filters__color-row">
                <span class="hat-filters__color-label">
                  colors
                </span>
                <div class="hat-filters__color-grid">
                  <div
                    v-for="color in hatColors"
                    :key="color.text"
                    class="hat-color-select">
                    <!-- the ol' hidden input hack -->
                    <input
                      :id="'hat-color-input__'+color.text"
                      v-model="filters.colors"
                      type="checkbox"
                      class="hat-color-select__input"
                      :value="color.text">
                    <label
                      v-ripple
                      :for="'hat-color-input__'+color.text"
                      :style="{
                        'backgroundColor': color.hexValue
                      }"
                      class="hat-color-select__label">
                      <v-icon
                        class="hat-color-select__checkmark"
                        :class="{
                          'hat-color-select__checkmark--black': color.text==='white'
                        }">
                        done
                      </v-icon>
                    </label>
                  </div>
                </div>
              </div>

              <span class="hat-filters__row-heading">
                styles
              </span>
              <div
                v-for="style in hatStyles"
                :key="style.text"
                v-ripple
                class="hat-filters__row">
                <v-checkbox
                  v-model="filters.styles"
                  color="primary"
                  class="hat-filters__checkbox"
                  :label="style.text"
                  :value="style.value"
                  hide-details />
              </div>
            </div>
          </div>
        </v-navigation-drawer>
      </div>

      <!-- LOADER -->
      <div
        v-if="loading"
        key="loading"
        class="hat-catalog__loading">
        <v-progress-circular
          size="100"
          indeterminate
          color="primary" />
      </div>

      <!-- CATALOG -->
      <div
        v-if="!loading"
        class="hat-catalog-layout__inner">
        <v-scroll-y-transition
          group
          mode="out-in"
          tag="div"
          class="hat-catalog">
          <div
            v-for="hat in paginatedHats"
            :key="hat.sku"
            v-ripple
            class="hat-listing"
            @click="goToHat(hat)">
            <div class="product-badges">
              <span
                v-if="hat.salePrice"
                class="product-badges__sale primary">
                SALE!
              </span>
              <span
                v-if="!isInStock(hat.quantityInStock)"
                class="product-badges__sold-out info">
                SOLD OUT
              </span>
              <span
                v-if="isLowQuantity(hat)"
                class="product-badges__low-quantity warning">
                only<span class="product-badges__quantity-counter">{{ displayInStock(hat.quantityInStock) }}</span>remaining!
              </span>
            </div>

            <v-img
              :src="imageDir + hat.images[0]"
              width="100%"
              aspect-ratio="1"
              class="hat-thumbnail"
              :class="{
                'hat-thumbnail--sold-out': !isInStock(hat.quantityInStock)
              }">
              <div
                slot="placeholder"
                class="hat-thumbnail__placeholder">
                <v-progress-circular
                  size="100"
                  indeterminate
                  color="primary" />
              </div>
            </v-img>

            <span
              class="hat-listing__name"
              :class="{
                'hat-listing__name--sold-out': !isInStock(hat.quantityInStock)
              }">
              {{ hat.name }}
            </span>
            <div v-if="hat.salePrice && isInStock(hat.quantityInStock)">
              <span class="hat-listing__retail-price hat-listing__retail-price--sale">
                {{ hat.retailPrice | price }}
              </span>
              <span class="hat-listing__sale-price">
                {{ hat.salePrice | price }}
              </span>
            </div>
            <div v-else>
              <span
                class="hat-listing__retail-price"
                :class="{
                  'hat-listing__retail-price--sold-out': !isInStock(hat.quantityInStock)
                }">
                {{ hat.retailPrice | price }}
              </span>
            </div>
          </div>
        </v-scroll-y-transition>

        <div
          v-if="!loading && hats.length > 0"
          class="load-more">
          <span class="load-more__page-details">
            Showing {{ pageItemCount }} of {{ filteredHats.length }} hats
          </span>
          <v-progress-linear
            v-model="catalogProgress"
            height="2"
            background-color="accent"
            class="load-more__progress" />
          <v-btn
            outline
            color="primary"
            class="load-more__load-more-button"
            :disabled="filteredHats.length === paginatedHats.length"
            @click="loadNextPage">
            Load More
          </v-btn>
          <v-btn
            flat
            small
            class="load-more__go-to-top"
            @click="scrollToTop">
            go to top
          </v-btn>
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'Home',
  data() {
    return {
      filterDrawer: null,
      filters: {
        inStock: false,
        onSale: false,
        colors: [],
        styles: [],
      },
      sortCriteria: [
        {
          text: 'Best Selling',
          value: 'quantitySoldRecently',
        },
        {
          text: 'Retail Price (lowest)',
          value: 'retailPrice',
        },
      ],
      currentSort: {},
      pageNumber: 1,
      pageLimit: 12,
    };
  },
  computed: {
    ...mapState('hats', {
      hats: 'hats',
      hatsConfig: 'hatsConfig',
      hatColors: 'hatColors',
      hatStyles: 'hatStyles',
      imageDir: 'imageDir',
      loading: 'loading',
      errorMessage: 'errorMessage',
      lowQuantityThreshold: 'lowQuantityThreshold',
    }),
    sortedHats() {
      const sortVal = this.currentSort.value;
      return this.hats.map(x => x).sort((a, b) => b[sortVal] - a[sortVal]);
    },
    filteredHats() {
      return this.sortedHats.filter((hat) => {
        // in stock
        if (this.filters.inStock === true) {
          return this.isInStock(hat.quantityInStock);
        }
        // on sale
        if (this.filters.onSale === true) {
          if (!hat.salePrice) return false;
        }
        // color filter
        if (this.filters.colors.length !== 0) {
          if (!this.filters.colors.includes(hat.attributes.color)) {
            return false;
          }
        }
        // style filter
        if (this.filters.styles.length !== 0) {
          if (!this.filters.styles.includes(hat.attributes.style)) {
            return false;
          }
        }
        return true;
      });
    },
    paginatedHats() {
      return this.filteredHats.filter((row, index) => {
        const limit = this.pageNumber * this.pageLimit;
        if (index < limit) return true;
        return false;
      });
    },
    catalogProgress() {
      return (
        ((this.pageNumber * this.pageLimit) / this.filteredHats.length) * 100
      );
    },
    pageItemCount() {
      return this.pageNumber * this.pageLimit >= this.filteredHats.length
        ? this.filteredHats.length
        : this.pageNumber * this.pageLimit;
    },
  },
  created() {
    this.setInitPageLimit();
    this.$store.dispatch('hats/getAllHats');
    this.$store.dispatch('hats/getHatColors');
    this.$store.dispatch('hats/getHatStyles');
    this.$store.dispatch('hats/getHatsConfig');
  },
  methods: {
    isInStock(quantity) {
      return quantity >= this.hatsConfig.inventoryBuffer;
    },
    displayInStock(quantity) {
      return quantity - this.hatsConfig.inventoryBuffer >= 0
        ? quantity - this.hatsConfig.inventoryBuffer
        : 0;
    },
    isLowQuantity(hat) {
      return this.displayInStock(hat.quantityInStock) <= this.lowQuantityThreshold && this.isInStock(hat.quantityInStock);
    },
    goToHat(item) {
      this.$router.push({
        name: 'hat-product',
        params: {
          slug: item.slug,
        },
      });
    },
    setInitPageLimit() {
      const pageWidth = window.innerWidth || document.body.clientWidth;
      if (pageWidth <= 768) {
        this.pageLimit = 12;
      } else {
        this.pageLimit = 36;
      }
    },
    loadNextPage() {
      this.pageNumber++;
    },
    scrollToTop() {
      // https://www.w3schools.com/howto/howto_js_scroll_to_top.asp
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    },
  },
};
</script>

<style lang="stylus" scoped>
@require('../stylus/base')

.hat-catalog-toolbar
  display: flex
  flex-flow: row nowrap
  align-items: center
  justify-content: space-between
  &--above-drawer
    position: relative
    z-index: 4

.hat-catalog-toolbar__filter-button,
.hat-filter-drawer__close-button
  @media screen and (min-width: 48rem)
    display: none

.hat-catalog-sort
  max-width: 25%
  @media screen and (max-width: 48rem)
    max-width: 35%


.hat-catalog-layout
  display: flex
  flex-flow: row nowrap
  align-items: stretch
  align-content: stretch
  justify-content: space-between
  width: 100%
  @media screen and (max-width: 48rem)
    flex-flow: column nowrap

  &__inner
    display: flex
    flex-flow: column nowrap
    align-items: center
    justify-content: space-between
    width: 100%

.hat-catalog
  width: 100%
  &__loading
    padding-top: 10rem
    padding-bottom: 10rem
    margin: 0 auto

// FILTER DRAWER
.hat-filter-drawer
  background-color: $white
  &__wrapper
    @media screen and (max-width: 48rem)
      position: absolute
      top: 0
      left: -1px
      height: 100%
      min-height: 100vh
      z-index: 0
      &--active
        z-index: 5

.hat-filters
  &__header-row
    display: flex
    flex-flow: row nowrap
    align-items: center
    justify-content: space-between

    padding-bottom: $base-padding
    padding-right: $base-padding
    @media screen and (max-width: 48rem)
      padding-bottom: 0
      padding-left: $base-padding*2

  &__body
    display: flex
    flex-flow: column nowrap
    align-items: flex-start
    justify-content: flex-start
  &__row
    display: flex
    flex-flow: row nowrap
    align-items: flex-start
    justify-content: flex-start
    width: 100%
    padding-top: $base-padding
    padding-bottom: $base-padding
    cursor: pointer
    @media screen and (max-width: 48rem)
      padding-left: $base-padding*2

  &__row-heading
    text-transform: capitalize
    width: 100%
    font-weight: $base-header-font-weight
    padding-top: $base-padding
    @media screen and (max-width: 48rem)
      padding-left: $base-padding*2
  &__checkbox
    margin-top: 0
    padding-top: 0
    text-transform: capitalize
    height: 100%
    width: 100%

  &__color-row
    @extends .hat-filters__row
    flex-flow: column nowrap
    padding-right: $base-padding*10
    @media screen and (max-width: 48rem)
      padding-right: $base-padding*8
  &__color-label
    text-transform: capitalize
    width: 100%
    font-weight: $base-header-font-weight

  &__color-grid
    display: flex
    flex-flow: row wrap
    align-items: flex-start
    justify-content: flex-start
    width: 100%
    padding-left: $base-padding*.5

.hat-color-select
  position: relative
  height: 2rem
  flex-basis: 20%
  margin-right: $base-padding*.5
  margin-bottom: $base-padding*.5

  &__label
    position: absolute
    top: 0
    left: 0
    z-index: 1
    height: 100%
    width: 100%
    display: flex
    align-items: center
    justify-content: center
    cursor: pointer
    border-radius: 50%
    box-shadow: $base-box-shadow
    transition: $base-transition

    &::before
      content: ''
      position: absolute
      top: 0
      left: 0
      height: 100%
      width: 100%
      opacity: .12
      border-radius: 50%
      transition: $base-transition

    &:hover::before
      background-color: $white

  &__checkmark
    color: $white
    visibility: hidden
    &--black
      color: $black

  &__input
    position: absolute
    top: 0
    left: 0
    z-index: 0
    opacity: 0
    &:checked + .hat-color-select__label
      box-shadow: none
    &:checked + .hat-color-select__label::before
        background-color: $black
    &:checked + .hat-color-select__label .hat-color-select__checkmark
      visibility: visible
      &--black
        color: $black

// .hat-listing styles are unscoped
.hat-listing
  &::before
    content: ''
    position: absolute
    top: 0
    left: 0
    height: 100%
    width: 100%
    z-index: 1
    background-color: $white
    opacity: 0
    transition: $base-transition
    cursor: pointer

  &:hover::before
    opacity: .24

  &__name
    font-size: .85rem
    text-transform: capitalize
    text-align: center

    padding-top: $base-padding*.5
    &--sold-out
      opacity:.6

  &__retail-price
    &--sale
      opacity: .5
      text-decoration: line-through
    &--sold-out
      opacity:.6

  &__sale-price
    color: $primary
    font-style: italic
    font-weight: $base-header-font-weight


.hat-thumbnail
  &__placeholder
    display: flex
    flex-flow: column nowrap
    align-items: center
    justify-content: center
    width: 100%
    height: 100%
  &--sold-out
    opacity:.6


// BADGES
.product-badges
  position: absolute
  top: 0
  left: -.1rem
  display: flex
  flex-flow: column nowrap
  align-items: flex-start
  justify-content: flex-start
  z-index: 1
  padding-top: $base-padding

  &__sale,
  &__low-quantity,
  &__sold-out
    margin-top: $base-padding*.5
    padding: $base-padding*.25 $base-padding*.5
    color: $white
    font-weight: bold
    font-size: .75rem
    // bg color set in template
    // with vuetify theme

  &__quantity-counter
    margin-right: $base-padding*.25
    margin-left: $base-padding*.25
    padding: 0 $base-padding*.25
    font-style: italic
    color: $primary
    background-color: $white
    border-radius: $base-border-radius

.load-more
  display: flex
  flex-flow: column nowrap
  align-items: center
  padding-top: $base-padding
  width: 100%

  &__page-details
    font-size: $font-size--small
    color: $accent--dark

  &__progress
    width: 25%
    margin: 0

  &__load-more-button
    width: 25%
    @media screen and (max-width: 48rem)
      width: 100%

  &__go-to-top
    font-size: $font-size--small
    color: $accent--dark
</style>

<style lang="stylus">
@require('../stylus/base')
/*
 * Vuetify data-iterator styles cannot be scoped
 */
.hat-catalog
  display: flex;
  flex-flow: row wrap
  align-items: flex-start
  justify-content: flex-start

  @media screen and (min-width: 48rem)
    margin-left: $base-padding

.hat-listing
  position: relative

  display: flex
  flex-flow: column nowrap
  align-items: center
  align-content: stretch
  justify-content: space-between
  flex-basis: 50% - ($base-padding)
  flex-shrink: 1
  @media screen and (min-width: 36rem)
    flex-basis: (100%/3) - ($base-padding)
  @media screen and (min-width: 48rem)
    flex-basis: 25% - ($base-padding)
  @media screen and (min-width: 100rem)
    flex-basis: (100%/6) - ($base-padding)

  margin-bottom: $base-padding
  padding: $base-padding*.5
</style>
